/**
 * WalkInJourney — the Walk-Ins customer page.
 *
 * ONE component, two mounts, exactly the arrangement `public-appointment.tsx`
 * describes for appointments:
 *
 *   preview  the Walk-Ins designer's own preview panel. Draft settings from the
 *            form under edit; REAL queue data (so the wait a designer sees is
 *            the wait a customer sees); and NOTHING is ever posted — no join,
 *            no leave, no position poll, and no timer that moves the position
 *            on its own. The ticket it draws is labelled as a sample.
 *   public   the standalone join page. Published settings for a slug, the same
 *            queue reads, and a REAL join before any ticket exists.
 *
 * `preview` is its own explicit boolean prop, deliberately NOT derived from the
 * Designer's `mode`. The existing Studio preview mounts the Designer with
 * `mode: "public"` to get the customer chrome, so a component that read `mode`
 * to decide whether a submit is real would treat every designer preview as a
 * customer and put staff-invented walk-ins in the tenant's own queue.
 *
 * Nothing here invents queue data. Services, service points, status and waits
 * all come from `getPublicQueue`; ticket, position and wait come from
 * `joinPublicQueue` / `getPublicQueuePosition`. Where a read returns nothing
 * the page shows an empty state, never a placeholder desk or a plausible
 * number.
 */
import React from "react";
import {
  Banner, Button, Checkbox, Chip, ChoiceTile, ConfirmDialog, Dropdown, Field,
  InlineEmptyState, Input, Pill, SectionLabel,
} from "@v0y4ge/jrni-ui";
import { QrCode } from "../../shell/QrCode";
import { DIAL } from "./dial-codes";
import { AppointmentFooter } from "./AppointmentFooter";
import { setJourneyComplete } from "./ReturnToOneStop";
import {
  clearWalkInCredentials, effectiveJoinFields, joinWalkInQueue, leaveWalkInQueue,
  newWalkInIntentKey, pollWalkInPosition, protectWalkInReferrer, queueAcceptsJoins,
  queueClosedReason, serviceOptionsOf, serviceWaitLine, waitLabel,
  writeWalkInCredentials,
  type WalkInConfig, type WalkInCredentials, type WalkInQueueRead,
  type WalkInServiceOption, type WalkInTicket,
} from "./alljourneys-walkins";

export type WalkInStep = "service" | "location" | "details" | "ticket";

export interface WalkInPresentation {
  /** The header bar's brand colour. Falls back to the DS primary token rather
   *  than a literal, so an unbranded tenant still renders in the product's own
   *  colour instead of a hardcoded one. */
  brandColor: string;
  logoText: string;
  showHeader: boolean;
  showFooter: boolean;
  hideJrni: boolean;
  /** The journey's own footer block, so "Powered by JRNI" obeys the same rule
   *  it does on the appointment page. */
  footer?: Record<string, unknown>;
  progressType: "none" | "bar" | "numbers" | "titles";
  progressShowCount: boolean;
}

export interface WalkInJourneyProps {
  /** TRUE in the designer. No POST of any kind is issued when this is set. */
  preview: boolean;
  config: WalkInConfig;
  customText: Record<string, string>;
  presentation: WalkInPresentation;
  /** The configured public slug. The real join REQUIRES it: its presence is
   *  what binds the join to this published journey's allowed queues and form
   *  policy. */
  slug: string;
  /** Canonical reads of the journey's assigned queues, already fetched by the
   *  caller (the designer refreshes them as the selection changes; the public
   *  mount reads them once on load). */
  queues: WalkInQueueRead[];
  /** True while the caller's own reads are still in flight. */
  loading?: boolean;
  /** A page-level failure (no published journey, queues unreadable, …). */
  error?: string;
  /** Preview only: the step the designer's step nav has selected. */
  step?: WalkInStep;
  /** Preview only: show the "left the line" outcome, which the designer's rail
   *  offers as its own entry. Preview-driven because the real page reaches
   *  this state by the visitor leaving, which a preview cannot do. */
  left?: boolean;
  onStepChange?: (step: WalkInStep) => void;
  /** Public only: credentials recovered from the URL on reload. */
  initialCredentials?: WalkInCredentials | null;
  /**
   * Public only: a queue PRE-SELECTED by the link that opened this page
   * (`?queue=<id>`), so a card that already named one desk does not ask again.
   *
   * A DEFAULT, NOT A LOCK. `reconcileSelection` re-checks it against the
   * journey's own readable queues once they load and drops it when the id is not
   * one of them, so a stale or foreign id falls back to the chooser rather than
   * selecting nothing; the visitor can change it either way.
   *
   * Distinct from `initialCredentials`, which is the `q`+`e` RESUME pair for a
   * ticket that already exists. This is a selection; that is a credential.
   */
  initialQueueId?: string | null;
  /** Public only: the service named by `?service=<id>` on the join link.
   *  A SELECTION, like `initialQueueId` — validated against the resolved
   *  queue's own options, and ignored (never an error) when it names nothing
   *  that queue offers. */
  initialServiceId?: string | null;
  /** Width the page lays itself out for; the designer passes the device the
   *  operator picked. */
  frameWidth?: string;
  /** Preview only: the designer draws its own frame, so the customer page
   *  renders without the standalone page's outer chrome. */
  embedded?: boolean;
}

interface WalkInJourneyState {
  step: WalkInStep;
  /** The canonical service id the join will carry — never a display label. */
  serviceId: string;
  /** The active category filter, "" for all. A real catalog category. */
  category: string;
  queueId: string;
  name: string;
  phone: string;
  /** ISO alpha-2 picked in the phone field's country selector. */
  phoneCountry: string;
  email: string;
  party: number;
  consent: boolean;
  touched: Record<string, boolean>;
  submitting: boolean;
  joinError: string;
  /** True only when the server READ the request and refused it. False for a
   *  timeout or a 5xx, where the entry may exist and the copy must not claim
   *  otherwise. */
  joinCertainlyFailed: boolean;
  fieldErrors: Record<string, string>;
  ticket: WalkInTicket | null;
  creds: WalkInCredentials | null;
  leaveAsking: boolean;
  leaving: boolean;
  leaveError: string;
  left: boolean;
  pollError: string;
  /** One key per join INTENT: a retry after a timeout reuses it, so the same
   *  intent cannot become two entries. Re-minted when the payload changes —
   *  see `intentKeyFor`; the key is bound to a payload fingerprint. */
  intentKey: string;
  /** Fingerprint of the payload `intentKey` was minted for. */
  intentFingerprint: string;
}

const POLL_MS = 20000;

function text(ct: Record<string, string>, key: string, fallback: string): string {
  const value = ct ? ct[key] : "";
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

/** A service belongs to a queue when THAT QUEUE lists it — matched on the
 *  canonical option `id`, which is what the join submits. Matching on the
 *  display name would drop a service whose catalog label differs from its id
 *  and silently make its desk ineligible. */
function eligibleQueues(queues: WalkInQueueRead[], serviceId: string): WalkInQueueRead[] {
  const readable = queues.filter((q) => q.view);
  if (!serviceId) return readable;
  return readable.filter((q) => serviceOptionsOf(q.view).some((o) => o.id === serviceId));
}

/** Every service the journey's queues offer, de-duplicated BY ID and in the
 *  order the queues declare them. */
function serviceUnion(queues: WalkInQueueRead[]): WalkInServiceOption[] {
  const out: WalkInServiceOption[] = [];
  for (const q of queues) {
    for (const option of serviceOptionsOf(q.view)) {
      if (!out.some((o) => o.id === option.id)) out.push(option);
    }
  }
  return out;
}

/** The real categories the offered services belong to, in first-seen order.
 *  A queue whose catalog resolved no category contributes none — the filter row
 *  is then not drawn at all rather than being filled with queue names. */
function categoriesOf(options: WalkInServiceOption[]): string[] {
  const out: string[] = [];
  for (const option of options) {
    if (option.category && out.indexOf(option.category) === -1) out.push(option.category);
  }
  return out;
}

function optionById(options: WalkInServiceOption[], id: string): WalkInServiceOption | null {
  return options.filter((o) => o.id === id)[0] || null;
}

function queueLabel(read: WalkInQueueRead): string {
  const view = read.view as unknown as Record<string, any> | null;
  const queue = (view && view.queue) || {};
  return String(queue.name || "") || read.id;
}

function queueSubLabel(read: WalkInQueueRead): string {
  const view = read.view as unknown as Record<string, any> | null;
  const queue = (view && view.queue) || {};
  const parts: string[] = [];
  if (queue.org) parts.push(String(queue.org));
  if (queue.status && queue.status !== "open") parts.push(queue.status === "paused" ? "Paused" : "Closed");
  else if (typeof queue.waiting === "number") parts.push(queue.waiting === 1 ? "1 waiting" : queue.waiting + " waiting");
  return parts.join(" · ");
}

function serviceWaitFor(read: WalkInQueueRead | undefined, service: string): number | null {
  const view = read && (read.view as unknown as Record<string, any> | null);
  const waits = view && view.queue && view.queue.serviceWaits;
  if (!waits || typeof waits !== "object") return null;
  const value = waits[service];
  return typeof value === "number" ? value : null;
}

/**
 * One row per member of `PublicEntryStatusEnum` — the vocabulary the join
 * response, the position poll and the public queue view all speak
 * (`waiting | called | serving | done | left | no-show`).
 *
 * `canLeave` mirrors the server: `leavePublicQueue` is "only valid when the
 * entry is `waiting` or `called`". `terminal` is where the visit is over and
 * the only useful action left is to join again.
 */
interface TicketState {
  heading: string;
  body: string;
  pillLabel: string;
  pill: "neutral" | "danger" | "success" | "warning";
  tone: "info" | "success" | "warning" | "danger" | "neutral";
  glyph: string;
  dotBg: string;
  dotFg: string;
  canLeave: boolean;
  terminal: boolean;
}

const GREEN_DOT = { dotBg: "var(--jrni-color-semantic-green-2)", dotFg: "var(--jrni-color-semantic-green-1)" };
const GREY_DOT = { dotBg: "var(--jrni-color-neutral-5)", dotFg: "var(--jrni-color-text-soft)" };

const TICKET_STATES: Record<string, TicketState> = {
  waiting: {
    heading: "You're in the queue", body: "", pillLabel: "Waiting", pill: "neutral",
    tone: "info", glyph: "✓", ...GREEN_DOT, canLeave: true, terminal: false,
  },
  called: {
    heading: "You've been called", body: "Go to the desk now — your ticket is being called.",
    pillLabel: "Called", pill: "success", tone: "success", glyph: "!", ...GREEN_DOT,
    canLeave: true, terminal: false,
  },
  serving: {
    heading: "You're being seen", body: "Someone is with you now.",
    pillLabel: "Being seen", pill: "success", tone: "success", glyph: "✓", ...GREEN_DOT,
    canLeave: false, terminal: false,
  },
  done: {
    heading: "This visit is finished", body: "Thanks for coming in. You can join the line again if you need anything else.",
    pillLabel: "Finished", pill: "neutral", tone: "neutral", glyph: "✓", ...GREY_DOT,
    canLeave: false, terminal: true,
  },
  left: {
    heading: "You have left the line", body: "Your ticket is no longer valid.",
    pillLabel: "Left the line", pill: "danger", tone: "danger", glyph: "–", ...GREY_DOT,
    canLeave: false, terminal: true,
  },
  "no-show": {
    heading: "Your ticket was closed", body: "You were called and the desk could not find you, so your place was given up. Join again if you still need to be seen.",
    pillLabel: "Missed", pill: "danger", tone: "warning", glyph: "–", ...GREY_DOT,
    canLeave: false, terminal: true,
  },
};

/** The platform purple every booking flow defaults to when the journey has no
 *  brand of its own — the same literal as `DEFAULTS.brandColor` (AllJourneys) and
 *  `BRAND_FALLBACK` (One Stop tab). */
export const JOURNEY_BRAND_DEFAULT = "#5142C7";
/** The dial-code picker never shrinks below what "🇺🇸 +1" needs, and the number
 *  field takes the rest — the picker was being squeezed illegible on phones. */
export const PHONE_COUNTRY_STYLE = { flex: "0 0 auto", minWidth: "108px" } as const;
export const PHONE_NUMBER_STYLE = { flex: "1 1 0", minWidth: 0 } as const;

export class WalkInJourney extends React.Component<WalkInJourneyProps, WalkInJourneyState> {
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private gone = false;
  /** Latches once the join link's preselection has had its chance (see
   *  `applyDeepLink`), so a later render cannot re-apply it over the visitor. */
  private deepLinkDone = false;

  constructor(props: WalkInJourneyProps) {
    super(props);
    this.state = {
      step: props.step || "service",
      serviceId: "",
      category: "",
      // The preview never reads the URL — same reason `creds` is null there.
      queueId: props.preview ? "" : (props.initialQueueId || ""),
      name: "", phone: "", phoneCountry: "US", email: "",
      party: 1,
      consent: false,
      touched: {},
      submitting: false,
      joinError: "",
      joinCertainlyFailed: true,
      fieldErrors: {},
      ticket: null,
      creds: props.preview ? null : (props.initialCredentials || null),
      leaveAsking: false,
      leaving: false,
      leaveError: "",
      left: !!props.left,
      pollError: "",
      intentKey: newWalkInIntentKey(),
      intentFingerprint: "",
    };
  }

  /** Same payload → same key (a retry recovers the first attempt's entry).
   *  Different payload → fresh key. Before this, the key minted at mount was
   *  sent with EVERY join regardless of what was typed, so a second join with
   *  changed details (or after a completed first join in the same page
   *  session) hit the server's IDEMPOTENCY_KEY_CONFLICT 409 — "This
   *  Idempotency-Key was already used with a different request" (Capture,
   *  2026-09-16). The fingerprint is compared here and never sent. */
  private intentKeyFor(payload: Record<string, unknown>): string {
    const fp = JSON.stringify(payload);
    if (fp === this.state.intentFingerprint) return this.state.intentKey;
    const key = newWalkInIntentKey();
    this.setState({ intentKey: key, intentFingerprint: fp });
    return key;
  }

  componentDidMount(): void {
    this.gone = false;
    if (this.props.preview) return;
    // The entry id in the query string is a capability. Suppress the referrer
    // BEFORE anything on this page can be clicked, not only after a join.
    protectWalkInReferrer();
    if (this.state.creds) {
      // Latch first: a resumed ticket must never be walked forward by the
      // deep-link preselect on a later render.
      this.deepLinkDone = true;
      this.setState({ step: "ticket" });
      this.refreshPosition();
      this.startPolling();
      return;
    }
    this.applyDeepLink();
  }

  /** The join link's preselection, applied ONCE, as soon as the queues resolve.
   *
   *  `join.<tenant>/?queue=<id>` already narrowed the journey to that desk and
   *  then stopped on "What" with one card and a disabled Continue — a step
   *  that asks a question with a single possible answer (CD, 2026-09-17). So:
   *
   *    · one service on the resolved queue → select it and start on "Your info"
   *    · `&service=<id>` naming one of its options → same
   *    · anything else — several services and no `service`, an id the queue does
   *      not offer, no parameters at all → unchanged, the chooser
   *
   *  A named service that the queue does not offer is a DEFAULT, not an error:
   *  the visitor gets the chooser scoped to the queue, which is what they would
   *  have had without the parameter.
   *
   *  Once only, and never in preview. `deepLinkDone` latches on the first run
   *  that sees queues, so the visitor pressing Back to "What" and choosing
   *  something else is not dragged forward again by the next render. */
  private applyDeepLink(): void {
    if (this.props.preview || this.deepLinkDone) return;
    /* A VISITOR WHO ALREADY HAS A TICKET is not deep-linking (CD, 2026-09-17).
       Latch and leave: their ticket QR carries `q`+`e` and no `queue`, so
       `wanted` was empty, every attached desk was in scope, and a world with
       one desk and one service resolved a "pick" that moved them off "ticket"
       onto "details" — a step they cannot come back from, because the public
       progress rail refuses forward moves. The same happened on any reload
       after joining through a `?queue=` link. Latching (not just returning)
       means a later render cannot retry it. */
    if (this.state.creds || this.state.ticket) { this.deepLinkDone = true; return; }
    const queues = this.props.queues || [];
    if (!queues.length) return;
    this.deepLinkDone = true;
    if (this.state.serviceId) return;
    const wanted = String(this.props.initialQueueId || "").trim();
    const named = String(this.props.initialServiceId || "").trim();
    /* NO PARAMETERS, NO PRESELECTION — the comment above always said so; the
       code did not (CD, 2026-09-17). An empty `wanted` scoped to every attached
       queue, so a plain visit to a one-service page skipped "What", and with
       Ask-which-service-point on and two desks it landed on "Your info" with no
       desk chosen. Preselection is what the LINK asked for, never something the
       page decides on its own. */
    if (!wanted && !named) return;
    const scoped = wanted ? queues.filter((q) => q.id === wanted) : queues;
    if (!scoped.length) return;
    /* THE DESK MUST BE ABLE TO TAKE THE VISIT (CD, 2026-09-17).

       `eligibleQueues` answers "which desks OFFER this service" and says
       nothing about whether they are open — so the guard below, which I
       described in 0917f as re-validating joinability, never did. A link to a
       closed desk therefore preselected anyway; on a page with a second, open
       desk the visitor landed on "Enter your details" showing the OTHER desk's
       service under the closed desk's name, with the other desk's opening
       time. On a single-queue page nothing visibly disagreed, which is why it
       surfaced only now.

       `queueAcceptsJoins` is the real test, and it is the same one
       `canSubmitJoin` uses — so the page can no longer walk a visitor to a
       form it will then refuse to submit. */
    const joinable = scoped.filter((q) => q.view && queueAcceptsJoins(q.view, q.status));
    if (!joinable.length) {
      /* The deeplinked desk cannot take anyone. Drop the pin as well as the
         preselect: leaving it pinned is what produced the incoherent screen —
         one desk's name above another desk's services. Unpinned, the visitor
         gets the ordinary chooser over the desks that CAN take them, which is
         what they would have had without the link. */
      if (this.state.queueId) this.setState({ queueId: "" });
      return;
    }
    const options = serviceUnion(joinable);
    const pick = named && options.some((o) => o.id === named)
      ? named
      : (options.length === 1 ? options[0]!.id : "");
    if (!pick) return;
    if (!eligibleQueues(joinable, pick).length) return;
    this.setState({ serviceId: pick, step: "details" });
  }

  componentDidUpdate(prev: WalkInJourneyProps): void {
    /* In PREVIEW the designer's step is authoritative, so it is synced whenever
       it differs from ours — not only on the render where it changed
       (2026-09-17, CD). The old guard also required `props.step !== prev.step`,
       which meant a step change that arrived on a render where React had
       already reconciled the prop (a rail click that re-renders the pane, a
       remount that seeds state from a since-superseded value) was dropped, and
       the preview sat on the old step while the toolbar caption showed the new
       one. The public mount is unaffected: `props.step` is preview-only, and
       the customer's own step lives in state. */
    void prev;
    /* The One Stop return strip warns before leaving an unfinished join (KP,
       2026-09-17). Public mount only: the ticket step with credentials is done. */
    if (!this.props.preview) {
      setJourneyComplete(this.state.step === "ticket" && !!(this.state.creds && this.state.creds.entryId));
    }
    if (this.props.preview && this.props.step && this.props.step !== this.state.step) {
      this.setState({ step: this.props.step });
    }
    if (this.props.preview && !!this.props.left !== this.state.left) {
      this.setState({ left: !!this.props.left });
    }
    // The queues load after mount on the public page, so the join link's
    // preselection gets its chance here too — `applyDeepLink` is idempotent.
    if (!this.props.preview) this.applyDeepLink();
    // A service the current queue set no longer offers cannot stay selected:
    // the details step would submit a service the chosen desk does not run and
    // the join would be refused at the server for a choice the page had already
    // shown as accepted.
    if (this.props.queues !== prev.queues) this.reconcileSelection(this.props.queues);
  }

  componentWillUnmount(): void {
    this.gone = true;
    this.stopPolling();
  }

  /* ── selection ─────────────────────────────────────────────────────── */

  private reconcileSelection(queues: WalkInQueueRead[]): void {
    this.setState((st) => {
      const services = serviceUnion(queues);
      const serviceId = st.serviceId && services.some((o) => o.id === st.serviceId) ? st.serviceId : "";
      const eligible = eligibleQueues(queues, serviceId);
      const queueId = st.queueId && eligible.some((q) => q.id === st.queueId) ? st.queueId : "";
      // A category that no longer exists in the new option set would hide every
      // service behind an active filter nobody can see the effect of.
      const cats = categoriesOf(services);
      const category = st.category && cats.indexOf(st.category) > -1 ? st.category : "";
      if (serviceId === st.serviceId && queueId === st.queueId && category === st.category) return null;
      return { serviceId, queueId, category } as Pick<WalkInJourneyState, "serviceId" | "queueId" | "category">;
    });
  }

  private eligible(): WalkInQueueRead[] {
    return eligibleQueues(this.props.queues, this.state.serviceId);
  }

  /** The queue a join would go to: the explicit pick, or the only eligible one.
   *  Never "the first of several" — with more than one and no pick, the page
   *  asks. */
  private chosenQueue(): WalkInQueueRead | null {
    const eligible = this.eligible();
    if (this.state.queueId) return eligible.filter((q) => q.id === this.state.queueId)[0] || null;
    return eligible.length === 1 ? eligible[0] : null;
  }

  /** The services on screen right now: every canonical option the attached
   *  queues resolved, narrowed by the active category filter. */
  private visibleServices(): WalkInServiceOption[] {
    const services = serviceUnion(this.props.queues);
    return this.state.category
      ? services.filter((o) => o.category === this.state.category)
      : services;
  }

  /**
   * The service a real join may carry: the current selection, but only while it
   * is still one of the options ON SCREEN.
   *
   * This is a service-selection journey — the reason for the visit is the thing
   * being chosen — so a join without one is not a valid submission. It used to
   * be possible in two ways, and both sent `service: null`, which the queue
   * worker resolves to its OWN configured default: a service nobody picked,
   * chosen by a fallback the visitor never saw.
   *
   *   · an unresolved catalogue offered nothing, and Continue was enabled
   *     anyway on `!services.length`;
   *   · a selection made before a category filter or a queue-set change was
   *     kept while hidden, and submitted from a step that no longer showed it.
   */
  private submittableService(): WalkInServiceOption | null {
    const id = this.state.serviceId;
    if (!id) return null;
    return this.visibleServices().filter((o) => o.id === id)[0] || null;
  }

  /** Everything a REAL join needs, in one answer: a visible service, a desk
   *  that runs it, and that desk currently taking people. */
  private canSubmitJoin(): boolean {
    if (!this.submittableService()) return false;
    const queue = this.chosenQueue();
    return !!queue && queueAcceptsJoins(queue.view, queue.status);
  }

  private steps(): WalkInStep[] {
    const list: WalkInStep[] = ["service"];
    // The service-point step only exists when it has something to ask: the
    // setting is on AND more than one queue could take this visit.
    if (this.props.config.qAskLocation && this.eligible().length > 1) list.push("location");
    list.push("details", "ticket");
    return list;
  }

  private goStep(step: WalkInStep): void {
    this.setState({ step });
    if (this.props.onStepChange) this.props.onStepChange(step);
  }

  /* ── the real calls (public mount only) ────────────────────────────── */

  private startPolling(): void {
    if (this.props.preview || this.pollTimer) return;
    this.pollTimer = setInterval(() => this.refreshPosition(), POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  }

  private refreshPosition(explicit?: WalkInCredentials | null): void {
    if (this.props.preview) return;
    const creds = explicit || this.state.creds;
    if (!creds) return;
    pollWalkInPosition(creds.queueId, creds.entryId).then((res) => {
      if (this.gone) return;
      if (res.ok) {
        this.setState({ ticket: res.ticket, pollError: "" });
        // Polling stops only at a TERMINAL status. `serving` is not one: the
        // entry is live, staff can still complete it, return it to the queue
        // or mark it a no-show, and a page that stopped polling there froze on
        // "You're being seen" for the rest of the visit and never noticed the
        // transition. `TICKET_STATES[...].terminal` is the single place that
        // decides, so the poll and what the ticket renders cannot disagree.
        if ((TICKET_STATES[res.ticket.status] || TICKET_STATES.waiting).terminal) this.stopPolling();
        return;
      }
      if (res.gone) {
        // The entry really is gone; the credential is worthless and keeping it
        // in the URL would keep re-showing a ticket that no longer exists.
        this.stopPolling();
        clearWalkInCredentials();
        this.setState({ creds: null, ticket: null, left: true, pollError: "" });
        return;
      }
      // TRANSIENT. The credentials are kept and polling continues — a network
      // blip must not cost someone their place on screen.
      this.setState({ pollError: res.message });
    });
  }

  private submitJoin = (): void => {
    if (this.props.preview) return;                      // ⛔ preview never posts
    const queue = this.chosenQueue();
    if (!queue || this.state.submitting) return;
    // No visible service, or a desk that is not taking people, is not a
    // submittable state. Guarded here as well as on the button so a stale
    // selection cannot be posted by a click that raced a filter change.
    const service = this.submittableService();
    if (!service || !queueAcceptsJoins(queue.view, queue.status)) return;
    const missing = this.missingFields();
    if (missing.length) {
      const touched: Record<string, boolean> = { ...this.state.touched };
      missing.forEach((key) => { touched[key] = true; });
      this.setState({ touched });
      return;
    }
    /* Shape as well as presence (CD, 2026-09-17). It only checked for EMPTY
       required fields, so a malformed address or a too-short number went to the
       server and came back a 400 — a round trip to learn something the page
       already knew. Every failing field is marked touched so all of them light
       up at once rather than one per press. */
    const problems = this.detailsErrors();
    const bad = Object.keys(problems);
    if (bad.length) {
      const touched: Record<string, boolean> = { ...this.state.touched };
      bad.forEach((key) => { touched[key] = true; });
      this.setState({ touched });
      return;
    }
    const payload = {
      queueId: queue.id,
      journeySlug: this.props.slug,
      name: this.state.name.trim(),
      phone: this.state.phone.trim() || undefined,
      /* The region the number belongs to, so the backend can normalise a
         nationally-formatted number. Omitted when there is no number. */
      phoneCountry: this.phoneCountryForSubmit(),
      email: this.state.email.trim() || undefined,
      service: service.id,
      party: this.props.config.qPartySize ? this.state.party : 1,
    };
    const idempotencyKey = this.intentKeyFor(payload);
    this.setState({ submitting: true, joinError: "", joinCertainlyFailed: true, fieldErrors: {} });
    joinWalkInQueue({ ...payload, idempotencyKey }).then((res) => {
      if (this.gone) return;
      if (!res.ok) {
        const fieldErrors: Record<string, string> = {};
        res.fields.forEach((f) => { if (f.path) fieldErrors[f.path] = f.message; });
        // `intentKey` is DELIBERATELY not reset here. The next press is the
        // same intent, and carrying the key is what makes a retry after a
        // timeout recover the entry the first attempt may already have made
        // instead of putting the same person in the line twice.
        this.setState({
          submitting: false,
          joinError: res.message,
          joinCertainlyFailed: res.certainlyNotJoined,
          fieldErrors,
        });
        return;
      }
      const creds = { queueId: res.ticket.queueId, entryId: res.ticket.entryId };
      writeWalkInCredentials(creds);
      this.setState({ submitting: false, ticket: res.ticket, creds, step: "ticket", left: false });
      this.startPolling();
      // The join response's position is a snapshot taken at insert; one
      // immediate poll replaces the derived people-ahead with the queue's own
      // current answer rather than leaving it until the first interval.
      // `creds` is passed explicitly because the setState above has not been
      // applied yet.
      this.refreshPosition(creds);
    });
  };

  private confirmLeave = (): void => {
    if (this.props.preview) { this.setState({ leaveAsking: false }); return; }
    const creds = this.state.creds;
    if (!creds || this.state.leaving) return;
    this.setState({ leaving: true, leaveError: "" });
    leaveWalkInQueue(creds.queueId, creds.entryId).then((res) => {
      if (this.gone) return;
      if (!res.ok) {
        // Still in the line. Saying otherwise would send someone away from a
        // desk that is still expecting them.
        this.setState({ leaving: false, leaveError: res.message });
        return;
      }
      this.stopPolling();
      clearWalkInCredentials();
      this.setState({ leaving: false, leaveAsking: false, left: true, creds: null, ticket: null });
    });
  };

  private rejoin = (): void => {
    /* `goStep` afterwards, not just `step` in this setState (CD, 2026-09-17):
       in PREVIEW the designer's `step` prop is authoritative every render, so
       a move the component makes on its own — this, and the details card's
       "Change" link — was snapped straight back unless the designer is told.
       `goStep` tells it. */
    this.setState({
      left: false, ticket: null, creds: null, step: "service",
      joinError: "", fieldErrors: {}, leaveError: "",
      // A new intent, so the rejoin is not deduplicated onto the entry that was
      // just given up.
      intentKey: newWalkInIntentKey(),
      intentFingerprint: "",
    }, () => { if (this.props.onStepChange) this.props.onStepChange("service"); });
  };

  /* ── validation ────────────────────────────────────────────────────── */

  /** Country options for the phone picker: the author's pinned list first (in
   *  the order they chose), then everything else alphabetically — the same
   *  shape the appointment journey builds. Flags are the author's call. */
  private dialOptions(): { value: string; label: string; supportingText?: string }[] {
    const cfg = this.props.config;
    const pinned = (cfg.phoneSuggestedCountries || []).slice(0, 10);
    const rank = (iso: string) => { const i = pinned.indexOf(iso); return i < 0 ? pinned.length : i; };
    return DIAL.slice()
      .sort((a, b) => (rank(a.iso) - rank(b.iso)) || a.name.localeCompare(b.name))
      /* Flag and dial code only (KP, 2026-09-17) — the ISO code made the
         trigger too wide to read on a narrow phone. The country name moves
         to the menu row's supporting line, where it still disambiguates +1. */
      .map((c) => ({ value: c.iso, label: (cfg.phoneCountryFlags !== false ? c.flag + " " : "") + "+" + c.dial, supportingText: c.name }));
  }

  /**
   * Which fields this page will not submit without.
   *
   * The AUTHORITY is the server: the join handler unions the queue's own
   * `settings.joinForm` with this journey's `askEmail`/`emailOptional` /
   * `askPhone`/`phoneOptional` and answers with `fields[]` when something is
   * missing. This is the client half of that same rule so the form asks before
   * a round trip; when the two disagree the server's refusal is what shows.
   */
  private missingFields(): string[] {
    const queue = this.chosenQueue();
    const fields = effectiveJoinFields(this.props.config, queue && queue.view);
    const out: string[] = [];
    if (!this.state.name.trim()) out.push("name");
    if (fields.phone.required && !this.state.phone.trim()) out.push("phone");
    if (fields.email.required && !this.state.email.trim()) out.push("email");
    return out;
  }

  /** The phone as typed, split into country + national digits.
   *  A typed `+41…` wins over the picker, so a number carrying its own dial
   *  code is read as the country it names. Mirrors the appointment journey's
   *  `parsePhone` — same table, same rule. */
  private parsePhone(raw: string, pickedIso: string) {
    const typed = String(raw || "");
    const digits = typed.replace(/\D/g, "");
    const intl = typed.trim().charAt(0) === "+";
    const picked = DIAL.filter((c) => c.iso === pickedIso)[0];
    let country = picked || DIAL[0]!;
    let national = digits;
    if (intl) {
      const hit = DIAL.slice().sort((a, b) => b.dial.length - a.dial.length)
        .filter((c) => digits.indexOf(c.dial) === 0)[0];
      if (hit) { country = hit; national = digits.slice(hit.dial.length); }
    }
    const len = national.length;
    return { country, national, len, intl, valid: len >= country.min && len <= country.max };
  }

  /** Everything wrong with the details form, keyed by field.
   *
   *  The server is still the authority — the join handler refuses a bad address
   *  or number with a 400 and `fields[]` — but a visitor standing at a desk
   *  should not need a round trip to be told their email has no "@"
   *  (CD, 2026-09-17). Shape checks match the appointment details step, so the
   *  two forms accept and refuse the same things. */
  private detailsErrors(): Record<string, string> {
    const queue = this.chosenQueue();
    const fields = effectiveJoinFields(this.props.config, queue && queue.view);
    const out: Record<string, string> = {};
    const name = this.state.name.trim();
    if (!name) out.name = "Please enter your name";
    else if (name.length < 2) out.name = "That is too short to be a name.";

    const email = this.state.email.trim();
    if (fields.email.show) {
      if (!email) { if (fields.email.required) out.email = "Please enter an email address"; }
      else if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email)) out.email = "Enter a valid email address, like you@example.com.";
    }

    const phone = this.state.phone.trim();
    if (fields.phone.show) {
      if (!phone) { if (fields.phone.required) out.phone = "Please enter a mobile number"; }
      else {
        const p = this.parsePhone(phone, this.state.phoneCountry);
        if (!p.valid) {
          out.phone = "That is not a valid " + p.country.name + " number — "
            + (p.country.min === p.country.max ? p.country.min + " digits" : p.country.min + "–" + p.country.max + " digits")
            + " expected.";
        }
      }
    }
    return out;
  }

  /** ISO alpha-2 to send beside the number, so the backend can resolve a
   *  national-format number. Omitted when there is no number to qualify. */
  private phoneCountryForSubmit(): string | undefined {
    const raw = this.state.phone.trim();
    if (!raw) return undefined;
    const iso = this.parsePhone(raw, this.state.phoneCountry).country.iso;
    return iso ? String(iso).toUpperCase() : undefined;
  }

  /* ── render ────────────────────────────────────────────────────────── */

  render(): React.ReactElement {
    const { customText: ct, presentation: pres, preview } = this.props;
    /* One default for every booking flow (KP, 2026-09-17): the same #5142C7 the
       appointment/event journeys (`DEFAULTS.brandColor`) and the One Stop tab
       (`BRAND_FALLBACK`) fall back to. This read `--jrni-color-primary-base`,
       a token that does not exist, so an unbranded walk-in journey painted its
       header and buttons with NO colour — white text on white. */
    const brand = pres.brandColor || JOURNEY_BRAND_DEFAULT;
    const frameWidth = this.props.frameWidth || "100%";
    const body = this.renderBody();

    const page = (
      <div
        data-testid="walkin-journey"
        data-mode={preview ? "preview" : "public"}
        style={{
          width: "100%", maxWidth: frameWidth, minHeight: "100%", boxSizing: "border-box",
          containerType: "inline-size", containerName: "journey-preview",
          display: "flex", flexDirection: "column",
          background: "var(--jrni-color-base-white)",
          border: "1px solid var(--jrni-color-surface-border)",
          borderRadius: "var(--jrni-radius-lg)", boxShadow: "var(--jrni-shadow-lg)",
          overflow: "hidden",
        }}
      >
        {pres.showHeader ? (
          <div data-testid="walkin-header" style={{ display: "flex", alignItems: "center", gap: "12px", rowGap: "8px", flexWrap: "wrap", padding: "14px 20px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: brand }}>
            {/* `presentation.logoText` ONLY — the journey's own persisted
                brand, resolved by `walkInBrandFrom` for both mounts. This used
                to prefer a `tenantName` prop that exists in the designer and
                cannot exist for an anonymous visitor, so the same journey
                showed the tenant's name in preview and the journey's in the
                page. */}
            <span style={{ fontSize: "15px", fontWeight: "700", color: "#FFFFFF", letterSpacing: "-0.01em" }}>
              {pres.logoText || "This workspace"}
            </span>
            <div style={{ flex: "1 1 auto" }} />
            <span style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.85)" }}>
              {text(ct, "progress.page", "Join the queue")}
            </span>
          </div>
        ) : null}
        {this.renderProgress(brand)}
        <div style={{ flex: "1 1 auto", display: "flex", alignItems: "flex-start", gap: "20px", padding: "22px 24px 28px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 620px", minWidth: "0", display: "flex", flexDirection: "column", gap: "18px" }}>
            {body}
          </div>
        </div>
        <AppointmentFooter
          footer={pres.footer as never}
          showCustom={pres.showFooter}
          hideJrni={pres.hideJrni}
        />
      </div>
    );

    if (this.props.embedded) return page;
    return (
      // FILLS its host, does not re-assert a viewport. The host page is already a
      // min-height:100vh flex column; a second 100vh here, under the 41px One Stop
      // return strip, made the document taller than the viewport by the strip plus
      // this padding, so the walk-in page always scrolled - even with nothing to
      // scroll to.
      <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "12px", background: "var(--jrni-color-surface-canvas)" }}>
        {page}
      </div>
    );
  }

  private renderProgress(brand: string): React.ReactNode {
    const pres = this.props.presentation;
    if (pres.progressType === "none") return null;
    const ct = this.props.customText;
    const order = this.steps();
    const labels: Record<WalkInStep, string> = {
      service: text(ct, "progress.svc", "What"),
      location: text(ct, "progress.loc", "Where"),
      details: text(ct, "progress.pd", "Your info"),
      ticket: text(ct, "progress.conf", "Your place in line"),
    };
    const index = Math.max(0, order.indexOf(this.state.step));

    /* ONE step rail for every customer journey (KP, 2026-09-16): titles only.
       The authored "bar" and "numbers" variants below are kept for reference
       but never reached; "none" still hides the rail. */
    const FORCE_TITLES = true;
    if (!FORCE_TITLES && pres.progressType === "bar") {
      const pct = Math.round(((index + 1) / order.length) * 100) + "%";
      return (
        <div style={{ padding: "16px 24px 4px" }}>
          <div style={{ height: "6px", borderRadius: "999px", background: "var(--jrni-color-neutral-5)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: pct, background: brand }} />
          </div>
        </div>
      );
    }

    if (!FORCE_TITLES && pres.progressType === "numbers") {
      return (
        <div className="apd-scroll" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px 24px 4px", overflowX: "auto", whiteSpace: "nowrap" }}>
          {order.map((id, i) => (
            <React.Fragment key={id}>
              <button
                type="button"
                onClick={() => this.onProgressClick(id, i, index)}
                style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "0", padding: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", color: i <= index ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)", fontWeight: i === index ? "700" : "500" }}
              >
                <span style={{ width: "22px", height: "22px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", background: i <= index ? brand : "var(--jrni-color-neutral-5)", color: i <= index ? "#FFFFFF" : "var(--jrni-color-text-soft)" }}>
                  {i + 1}
                </span>
                {labels[id]}
                {this.props.presentation.progressShowCount && i === index ? (
                  <span style={{ color: "var(--jrni-color-text-soft)", fontWeight: "500" }}>{" (" + (i + 1) + " of " + order.length + ")"}</span>
                ) : null}
              </button>
              {i < order.length - 1 ? (
                <span aria-hidden="true" style={{ flex: "0 0 24px", height: "2px", borderRadius: "999px", background: i < index ? brand : "var(--jrni-color-neutral-5)" }} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", padding: "16px 24px 0", minWidth: 0 }}>
        {order.map((id, i) => (
          <button
            key={id}
            type="button"
            onClick={() => this.onProgressClick(id, i, index)}
            style={{ background: "none", border: "0", padding: "0 0 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", textAlign: "left", borderBottom: `2px solid ${i === index ? brand : "transparent"}`, color: i <= index ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)", fontWeight: i === index ? "700" : "500" }}
          >
            {labels[id]}
          </button>
        ))}
      </div>
    );
  }

  /** Going FORWARD past a step the visitor has not completed would show a
   *  ticket nobody holds, so only completed steps (and, in the designer, every
   *  step) are reachable from the indicator. */
  private onProgressClick(id: WalkInStep, i: number, current: number): void {
    if (this.props.preview || i <= current) this.goStep(id);
  }

  private renderBody(): React.ReactNode {
    if (this.props.error) {
      return <Banner tone="danger" title="This page is unavailable">{this.props.error}</Banner>;
    }
    if (this.props.loading) {
      return <div role="status" style={{ padding: "18px 0", color: "var(--jrni-color-text-soft)" }}>Loading…</div>;
    }
    if (this.state.left) return this.renderLeft();
    switch (this.state.step) {
      case "location": return this.renderLocation();
      case "details": return this.renderDetails();
      case "ticket": return this.renderTicket();
      default: return this.renderService();
    }
  }

  /* ── service step ──────────────────────────────────────────────────── */

  private renderService(): React.ReactNode {
    const ct = this.props.customText;
    const cfg = this.props.config;
    const readable = this.props.queues.filter((q) => q.view);
    const failed = this.props.queues.filter((q) => !q.view);
    const services = serviceUnion(this.props.queues);
    const categories = categoriesOf(services);
    const visible = this.visibleServices();
    const info = text(ct, "service.info", "");
    const waitPrefix = text(ct, "service.wait", "Wait about");

    return (
      <>
        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
          {text(ct, "service.hdr", "What do you need help with?")}
        </h2>
        {info ? <p style={{ margin: "0", fontSize: "14px", color: "var(--jrni-color-text-body)" }}>{info}</p> : null}
        {failed.length ? (
          <Banner tone="warning" size="compact" title="Some service points could not be loaded">
            {failed.length === 1 ? "One service point is unavailable right now." : failed.length + " service points are unavailable right now."}
          </Banner>
        ) : null}
        {!readable.length ? (
          <InlineEmptyState title="Nothing to join right now" description="This page has no service point available. Please try again later." />
        ) : null}
        {readable.length && !services.length ? (
          <div role="status" style={{ border: "1px dashed var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", padding: "18px 16px", color: "var(--jrni-color-text-body)", background: "var(--jrni-color-surface-card)" }}>
            No reasons for visit are listed for this line yet.
          </div>
        ) : null}
        {/* REAL catalog categories, from `serviceOptions[].category`. The
            filter row used to be the queue names, which is a different axis
            entirely — picking "Records Window" there narrowed nothing and
            looked like a category that did not exist. A queue whose catalog
            resolved no categories draws no row at all. */}
        {cfg.showCategories && categories.length ? (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Chip selected={!this.state.category} onClick={() => this.pickCategory("")}>All</Chip>
            {categories.map((cat) => (
              <Chip key={cat} selected={this.state.category === cat} onClick={() => this.pickCategory(cat)}>{cat}</Chip>
            ))}
          </div>
        ) : null}
        {/* SERVICE DISPLAY is honoured (2026-09-17, CD). The appointment
            journey's card is still the one card — title, duration / wait line in
            the brand colour, description, image on top when the page shows
            images and the catalogue has one — but `serviceDisplay` now chooses
            how those cards are laid out, because the setting was persisted and
            read by nothing: the panel offered Cards / List and the page drew
            one column either way.

              Cards — a 2-up grid (1-up under 420px), image on top.
              List  — one row per service: a small square thumbnail beside the
                      text rather than a banner above it, so a long catalogue
                      scans in a screen instead of three.

            Selected is identical in both: 2px brand border + ring + check. */}
        {visible.length ? (
          <div style={{ display: "grid", gap: "10px", gridTemplateColumns: cfg.serviceDisplay === "cards" ? "repeat(auto-fit, minmax(min(200px, 100%), 1fr))" : "minmax(0, 1fr)" }}>
            {visible.map((option) => {
              const selected = this.state.serviceId === option.id;
              const wait = cfg.qWaitPerService ? this.waitForService(option.id) : null;
              const meta = [option.duration, wait !== null ? serviceWaitLine(wait, cfg.qWaitDisplay, waitPrefix) : ""].filter(Boolean).join(" · ");
              const asRow = cfg.serviceDisplay !== "cards";
              const img = cfg.showServiceImages && option.image ? option.image : "";
              return (
                <div
                  key={option.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  data-service-id={option.id}
                  onClick={() => this.pickService(option.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.pickService(option.id); } }}
                  style={{ position: "relative", display: "flex", flexDirection: asRow ? "row" : "column", alignItems: asRow ? "stretch" : undefined, cursor: "pointer", overflow: "hidden", background: "var(--jrni-color-surface-card)", border: `${selected ? "2px" : "1px"} solid ${selected ? "var(--jrni-color-primary-base)" : "var(--jrni-color-surface-border)"}`, boxShadow: selected ? "0 0 0 3px var(--jrni-color-primary-3), var(--jrni-shadow-md)" : "var(--jrni-shadow-chrome)", borderRadius: "var(--jrni-radius-md)" }}
                >
                  {img ? (
                    <img src={img} alt="" style={asRow
                      ? { width: "84px", flexShrink: 0, alignSelf: "stretch", display: "block", objectFit: "cover" }
                      : { width: "100%", display: "block", aspectRatio: "16 / 9", objectFit: "cover" }} />
                  ) : null}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: asRow ? "12px 14px" : "14px 16px 16px", minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <div style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.3", letterSpacing: "-0.01em" }}>{option.name}</div>
                      {selected ? (
                        <span aria-hidden="true" style={{ flexShrink: 0, width: "24px", height: "24px", borderRadius: "999px", background: this.props.presentation.brandColor || JOURNEY_BRAND_DEFAULT, color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", lineHeight: "1" }}>{"\u2713"}</span>
                      ) : null}
                    </div>
                    {meta ? (
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-primary-text)" }}>{meta}</div>
                    ) : null}
                    {option.description ? (
                      <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{option.description}</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        {services.length && !visible.length ? (
          <InlineEmptyState title="Nothing in that category" description="Choose another category, or clear the filter." />
        ) : null}
        {/* Continue needs a service that is ON SCREEN and chosen. It used to be
            enabled on `!services.length` as well, so an unresolved catalogue
            walked the visitor to the details step and joined with
            `service: null` — which the queue worker resolves to its own
            configured default, a service nobody picked. An empty catalogue is
            a dead end on a journey whose whole purpose is picking the reason
            for the visit, and it now reads as one. */}
        {this.renderContinue(!!this.submittableService())}
      </>
    );
  }

  /**
   * The live estimate for one service, from whichever eligible queue reports
   * it. `serviceWaits` is the queue's own calculation; there is no client-side
   * arithmetic behind this number.
   *
   * The map may be keyed by either the canonical id or the display name
   * depending on how the queue stored its refs, so both are tried — and only
   * a number the queue actually returned is ever shown.
   */
  private waitForService(serviceId: string): number | null {
    const option = optionById(serviceUnion(this.props.queues), serviceId);
    for (const q of eligibleQueues(this.props.queues, serviceId)) {
      const byId = serviceWaitFor(q, serviceId);
      if (byId !== null) return byId;
      if (option && option.name !== serviceId) {
        const byName = serviceWaitFor(q, option.name);
        if (byName !== null) return byName;
      }
    }
    return null;
  }

  /** Filtering is presentation only: a chosen service that the new filter
   *  hides stays chosen, because the visitor did choose it. What must not
   *  survive is a chosen service the QUEUES no longer offer — that is
   *  `reconcileSelection`'s job. */
  private pickCategory(category: string): void {
    this.setState({ category });
  }

  /** Select a service BY CANONICAL ID. An id no attached queue resolved is not
   *  a selection — it would be submitted verbatim as `service` and attach the
   *  entry to something the desk never offered — so it clears instead. */
  private pickService(serviceId: string): void {
    this.setState((st) => {
      const known = serviceId ? optionById(serviceUnion(this.props.queues), serviceId) : null;
      const next = known ? serviceId : "";
      const eligible = eligibleQueues(this.props.queues, next);
      // A previously chosen desk that does not run the new service is dropped
      // rather than carried into a join the server would refuse.
      const queueId = st.queueId && eligible.some((q) => q.id === st.queueId) ? st.queueId : "";
      return { serviceId: next, queueId };
    });
  }

  /* ── service-point step ────────────────────────────────────────────── */

  private renderLocation(): React.ReactNode {
    const ct = this.props.customText;
    const eligible = this.eligible();
    return (
      <>
        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
          {text(ct, "location.hdr", "Choose a service point")}
        </h2>
        <Field label="Service point">
          <Dropdown
            value={this.state.queueId}
            placeholder="Select a service point"
            onChange={(value: string) => this.setState({ queueId: value })}
            options={eligible.map((q) => ({
              value: q.id,
              // The queue's OWN name and its own live line length. Never a
              // fabricated desk name.
              label: queueLabel(q),
              supportingText: queueSubLabel(q) || undefined,
            }))}
          />
        </Field>
        <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "minmax(0, 1fr)" }}>
          {eligible.map((q) => (
            <ChoiceTile
              key={q.id}
              showCheck={false}
              label={queueLabel(q)}
              sublabel={queueSubLabel(q)}
              selected={this.state.queueId === q.id}
              trailing={queueAcceptsJoins(q.view, q.status) ? null : <Pill tone="neutral">Not taking anyone</Pill>}
              onSelect={() => this.setState({ queueId: q.id })}
              full={true}
            />
          ))}
        </div>
        {this.renderContinue(!!this.state.queueId)}
      </>
    );
  }

  /* ── details step ──────────────────────────────────────────────────── */

  private renderDetails(): React.ReactNode {
    const ct = this.props.customText;
    const cfg = this.props.config;
    const queue = this.chosenQueue();
    const closed = queue ? !queueAcceptsJoins(queue.view, queue.status) : false;
    const closedReason = queue ? queueClosedReason(queue.view, queue.status) : "";
    const errs = this.state.fieldErrors;
    const missing = this.state.touched;
    /* Shape errors computed every render, shown only once the field has been
       touched — so the form never scolds someone mid-type, but also never
       waits for the server to say the address has no "@". */
    const liveErrs = this.detailsErrors();
    /* The platform sentence, never the authored one — identical on all three journeys (KP, 2026-09-17). */
    const consentText = "I consent to my data being used for the purposes of this booking.";
    // The UNION of this page's settings and the chosen desk's own join form —
    // the same union the join handler validates against.
    const fields = effectiveJoinFields(cfg, queue && queue.view);
    const serviceName = (optionById(serviceUnion(this.props.queues), this.state.serviceId) || { name: "" }).name;

    return (
      <>
        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
          {text(ct, "details.hdr", "Enter your details")}
        </h2>
        {queue ? (
          /* Same "what you're booking" card as the appointment and event
             details pages (KP, 2026-09-16): service as the title, service
             point as the line under it, Change returns to the service step. */
          <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
            <div style={{ flex: "1 1 auto", minWidth: "0" }}>
              <div style={{ fontSize: "14px", fontWeight: "700" }}>{serviceName || queueLabel(queue)}</div>
              {serviceName ? <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{queueLabel(queue)}</div> : null}
            </div>
            <Button variant="link" size="sm" onClick={() => this.goStep("service")}>Change</Button>
          </div>
        ) : (
          <Banner tone="warning" size="compact" title="Choose a service point">
            Pick where you would like to be seen before joining the line.
          </Banner>
        )}
        {/* A selection can stop being on screen after a filter or a queue-set
            change. Without this the Join button was simply dead with no
            reason given. */}
        {!this.props.preview && !this.submittableService() ? (
          <Banner tone="warning" size="compact" title="Choose what you need help with">
            Go back and pick a reason for your visit before joining the line.
          </Banner>
        ) : null}
        {/* The queue's OWN reason, from its live status read — closed, paused,
            or shut until a stated time. The authored "line is full" copy is
            only used when the queue gives no reason of its own. */}
        {closed ? (
          <Banner tone="warning" size="compact" title="Not taking anyone right now">
            {closedReason || text(ct, "queue.closed", "The line is full for today. Please come back tomorrow.")}
          </Banner>
        ) : null}
        {this.state.joinError ? (
          <Banner
            tone="danger"
            size="compact"
            title={this.state.joinCertainlyFailed ? "You were not added to the line" : "This page could not confirm your place"}
          >
            {this.state.joinError}
          </Banner>
        ) : null}
        <Field label="Full name" required error={errs.name || (missing.name ? liveErrs.name : undefined)}>
          <Input value={this.state.name} onChange={(e: any) => this.setState({ name: e && e.target ? e.target.value : e })}
            onBlur={() => this.setState((s) => ({ touched: { ...s.touched, name: true } }))} full={true} />
        </Field>
        {/* Shown when EITHER this page asks for it or the desk's own join form
            does. A journey cannot hide a field its queue requires: the join is
            validated against the union, so hiding one produces a refusal on a
            question the visitor was never asked. */}
        {fields.phone.show ? (
          <Field
            label="Mobile number"
            required={fields.phone.required}
            hint={errs.phone || (missing.phone && liveErrs.phone) ? undefined
              : (fields.phone.required
                ? (fields.phone.enforcedByQueue ? "Required by this service point. Pick the country, then the number as dialled locally." : "Pick the country, then the number as it is dialled locally.")
                : "Optional — we will text you when it is nearly your turn.")}
            error={errs.phone || (missing.phone ? liveErrs.phone : undefined)}
          >
            {/* The appointment journey's shape: dial-code picker + national
                number (CD, 2026-09-17). It was a bare text Input, so a visitor
                typing a local number left the browser with nothing to resolve
                the region against. `phoneCountry` now travels on the join body
                beside the number. Flags and the pinned country list are the
                author's, via `phoneCountryFlags` / `phoneSuggestedCountries`. */}
            <div style={{ display: "flex", gap: "8px" }}>
              <Dropdown
                options={this.dialOptions()}
                value={this.parsePhone(this.state.phone, this.state.phoneCountry).country.iso}
                onChange={(v: any) => this.setState({ phoneCountry: String(v) })}
                aria-label="Country code"
                style={PHONE_COUNTRY_STYLE}
              />
              <Input
                type="tel"
                value={this.state.phone}
                onChange={(e: any) => this.setState({ phone: e && e.target ? e.target.value : e })}
                onBlur={() => this.setState((s) => ({ touched: { ...s.touched, phone: true } }))}
                style={PHONE_NUMBER_STYLE}
                full={true}
              />
            </div>
          </Field>
        ) : null}
        {fields.email.show ? (
          <Field
            label="Email address"
            required={fields.email.required}
            hint={fields.email.required
              ? (fields.email.enforcedByQueue ? "Required by this service point." : undefined)
              : "Optional."}
            error={errs.email || (missing.email ? liveErrs.email : undefined)}
          >
            <Input value={this.state.email} onChange={(e: any) => this.setState({ email: e && e.target ? e.target.value : e })}
              onBlur={() => this.setState((s) => ({ touched: { ...s.touched, email: true } }))} full={true} />
          </Field>
        ) : null}
        {cfg.qPartySize ? (
          <Field label="How many people" hint={"Up to " + cfg.qMaxParty + "."} error={errs.party || undefined}>
            <Dropdown
              value={String(this.state.party)}
              onChange={(value: string) => {
                const n = parseInt(value, 10);
                this.setState({ party: Math.max(1, Math.min(cfg.qMaxParty, isNaN(n) ? 1 : n)) });
              }}
              options={Array.from({ length: Math.max(1, cfg.qMaxParty) }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
            />
          </Field>
        ) : null}
        {/* Same consent block as the appointment and event details pages
            (KP, 2026-09-16): bold header, checkbox, platform default text. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
          <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>Data protection policy</h3>
          <Checkbox checked={this.state.consent} onChange={(v: any) => this.setState({ consent: v && v.target ? v.target.checked : !!v })} label={consentText} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "6px" }}>
          <Button variant="secondary" onClick={() => this.goStep(this.prevStep())}>Back</Button>
          <div style={{ flex: "1 1 auto" }} />
          <Button
            hierarchy="primary"
            onClick={this.props.preview ? () => this.goStep("ticket") : this.submitJoin}
            disabled={this.state.submitting || (!this.props.preview && (!this.canSubmitJoin() || !this.state.consent))}
          >
            {this.state.submitting ? "Joining…" : text(ct, "details.cta", "Join the queue")}
          </Button>
        </div>
        {this.props.preview ? (
          <p style={{ margin: "0", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>
            Preview — continuing shows a sample ticket. Nobody is added to the line.
          </p>
        ) : null}
      </>
    );
  }

  private prevStep(): WalkInStep {
    const order = this.steps();
    const i = Math.max(0, order.indexOf(this.state.step) - 1);
    return order[i];
  }

  private renderContinue(enabled: boolean): React.ReactNode {
    const order = this.steps();
    const i = order.indexOf(this.state.step);
    const next = order[Math.min(order.length - 1, i + 1)];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "6px" }}>
        {i > 0 ? <Button variant="secondary" onClick={() => this.goStep(this.prevStep())}>Back</Button> : null}
        <div style={{ flex: "1 1 auto" }} />
        <Button hierarchy="primary" disabled={!enabled} onClick={() => this.goStep(next)}>
          {text(this.props.customText, "progress.cta", "Continue")}
        </Button>
      </div>
    );
  }

  /* ── ticket ────────────────────────────────────────────────────────── */

  /** The display name for the service the entry is queued for. Prefers the
   *  server's echo, falls back to the local pick, and resolves both through
   *  the queue's own options; an id with no matching option is shown as-is
   *  rather than dropped. */
  private ticketServiceName(echoed: string | null): string {
    const id = echoed || this.state.serviceId;
    if (!id) return "";
    const option = optionById(serviceUnion(this.props.queues), id);
    return option ? option.name : id;
  }

  private renderTicket(): React.ReactNode {
    const ct = this.props.customText;
    const cfg = this.props.config;
    const preview = this.props.preview;
    const ticket = this.state.ticket;

    // A REAL ticket only ever comes from a real join. In the designer there is
    // none, so the page shows a clearly-labelled sample of the same layout.
    if (!preview && !ticket) {
      return (
        <>
          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
            {text(ct, "queue.hdr", "You're in the queue")}
          </h2>
          <Banner tone="warning" size="compact" title="No place in the line yet">
            Your details have not been sent. Go back and join the line to get a ticket.
          </Banner>
          <Button variant="secondary" onClick={() => this.goStep("details")}>Back to your details</Button>
        </>
      );
    }

    const shown = ticket || {
      entryId: "", queueId: "", ticket: "A-000", position: 4, peopleAhead: 3,
      estWaitMins: 12, status: "waiting", serviceId: null, recoveredExisting: false,
    };
    const wait = waitLabel(shown.estWaitMins, cfg.qWaitDisplay);
    /* THE QR IS THE CHECK-IN CODE (KP, 2026-09-17). It encoded this page's own
       status URL (`?q=…&e=…`); My Day's scanner reduces a URL to its LAST PATH
       SEGMENT and resolves that as an entry ref, so scanning a ticket resolved
       the slug "walk-in" and failed. It now carries the bare entry id — the
       value `resolveOwnCalendarEntry` takes — so a member scans the student's
       phone and checks them in for their service. The status page is still one
       tap away for the visitor: it is the page they are already on. */
    const checkInCode = this.state.creds && this.state.creds.entryId ? String(this.state.creds.entryId) : "";
    // The entry's ACTUAL status drives this whole screen. It used to be
    // ignored: a customer who had been called, was already being served, or
    // whose entry had been closed by staff kept reading "You're in the queue"
    // with a stale position underneath, and was still offered a Leave button
    // for a place they no longer held.
    const state = TICKET_STATES[shown.status] || TICKET_STATES.waiting;
    const stillQueued = shown.status === "waiting";
    const finished = state.terminal;

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: state.dotBg, color: state.dotFg, fontSize: "13px", fontWeight: "700" }}>{state.glyph}</span>
          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
            {shown.status === "waiting" ? text(ct, "queue.hdr", "You're in the queue") : state.heading}
          </h2>
          {preview ? <Pill tone="neutral">Sample ticket</Pill> : null}
          {!preview && shown.status !== "waiting" ? <Pill tone={state.pill}>{state.pillLabel}</Pill> : null}
        </div>
        {!preview && state.body ? (
          <Banner tone={state.tone} size="compact" title={state.heading}>{state.body}</Banner>
        ) : null}
        {preview ? (
          <p style={{ margin: "0", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>
            Preview — these numbers are placeholders for layout. A customer&rsquo;s ticket, position and wait come from the queue itself.
          </p>
        ) : null}
        {this.state.pollError ? (
          <Banner tone="warning" size="compact" title="Showing your last known place">{this.state.pollError}</Banner>
        ) : null}
        {this.state.leaveError ? (
          <Banner tone="danger" size="compact" title="You are still in the line">{this.state.leaveError}</Banner>
        ) : null}
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
          {cfg.qShowTicket ? (
            <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", color: "#FFFFFF", background: this.props.presentation.brandColor || JOURNEY_BRAND_DEFAULT }}>
              <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", opacity: "0.85" }}>Ticket</div>
              <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em" }}>{shown.ticket || "—"}</div>
            </div>
          ) : null}
          {/* Position and wait mean nothing once the entry has left the line —
              `QueueEntryPositionResponse.position` is explicitly null then, and
              `estWaitMins` is 0. Drawing the tiles anyway printed a confident
              "—" and "No wait" beside a heading that said the visit was
              finished. */}
          {cfg.qShowPos && stillQueued ? (
            <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)" }} />
                <span style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>
                  {text(ct, "queue.pos", "Position in line")}
                </span>
              </div>
              <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em", paddingTop: "4px" }}>
                {shown.position == null ? "—" : shown.position}
              </div>
            </div>
          ) : null}
          {wait && stillQueued ? (
            <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
              <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>
                {text(ct, "queue.wait", "Estimated wait")}
              </div>
              <div style={{ fontSize: "22px", fontWeight: "700", lineHeight: "1.4", paddingTop: "6px" }}>{wait}</div>
            </div>
          ) : null}
        </div>
        {stillQueued ? (
          <Banner tone="info" size="compact" title="Stay nearby">
            {text(ct, "queue.notify", "We'll text you when you're next to be seen.")}
          </Banner>
        ) : null}
        {/* The service's NAME, resolved from the queue's own options. The id is
            what was submitted; printing it here showed a customer a catalog
            key. `serviceId` on the ticket is the server's echo and wins over
            the local pick when they differ. */}
        {this.ticketServiceName(shown.serviceId) ? (
          <div style={{ fontSize: "13px", lineHeight: "1.5" }}>
            <SectionLabel tone="soft">Reason for visit</SectionLabel>
            <div style={{ fontWeight: "600", paddingTop: "4px" }}>{this.ticketServiceName(shown.serviceId)}</div>
          </div>
        ) : null}
        {cfg.showQr ? (
          checkInCode ? (
            <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
              <QrCode value={checkInCode} size={108} testid="walkin-checkin-qr" />
              <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)" }}>
                <div style={{ fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>Check in with this code</div>
                <div>Show it at the desk when you arrive — staff scan it to check you in.</div>
              </div>
            </div>
          ) : (
            // No entry, no URL. A square that resolves to nothing is worse than
            // none at all, so the panel says what it will show instead.
            <div style={{ padding: "14px", border: "1px dashed var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
              The QR code appears here once someone joins. It carries their check-in code, so staff can scan it on My Day.
            </div>
          )
        ) : null}
        {/* Leaving is only offered while it is a real action. `leavePublicQueue`
            is valid for a `waiting` or `called` entry and 404s otherwise, so a
            button on a served or closed entry is one that can only fail. A
            finished visit gets the rejoin action instead. */}
        {cfg.qAllowLeave && !preview && state.canLeave ? (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
            <Button hierarchy="destructive" disabled={this.state.leaving} onClick={() => this.setState({ leaveAsking: true })}>
              {text(ct, "queue.leave", "Leave the queue")}
            </Button>
          </div>
        ) : null}
        {cfg.qAllowLeave && preview ? (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
            <Button hierarchy="destructive" disabled={true}>{text(ct, "queue.leave", "Leave the queue")}</Button>
          </div>
        ) : null}
        {!preview && finished ? (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
            <Button hierarchy="primary" onClick={this.rejoin}>{text(ct, "queue.lvagain", "Join the line again")}</Button>
          </div>
        ) : null}
        <ConfirmDialog
          open={this.state.leaveAsking}
          onCancel={() => this.setState({ leaveAsking: false })}
          onConfirm={this.confirmLeave}
          tone="danger"
          icon={false}
          title={text(ct, "queue.lvttl", "Leave the line?")}
          consequence="Your ticket stops being valid and you lose your place."
          confirmLabel={text(ct, "queue.leave", "Leave the queue")}
          cancelLabel={text(ct, "queue.lvkeep", "Stay in the line")}
        />
      </>
    );
  }

  private renderLeft(): React.ReactNode {
    const ct = this.props.customText;
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>
            {text(ct, "queue.lvhdr2", "You are no longer in the queue")}
          </h2>
          <Pill tone="danger">Left the line</Pill>
        </div>
        <Banner tone="danger" size="compact" title={text(ct, "queue.lvhdr", "You have left the line")}>
          {text(ct, "queue.lvtxt", "Your ticket is no longer valid. You can join again if you still need to be seen.")}
        </Banner>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px" }}>
          <Button hierarchy="primary" onClick={this.rejoin}>{text(ct, "queue.lvagain", "Join the line again")}</Button>
        </div>
      </>
    );
  }
}

export default WalkInJourney;
